import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { useMarket } from '../hooks/useMarket';
import {
  BookOpen, Wrench, Hammer, TrendingUp, Award, FileText, Search,
  Plus, Trash2, Flag, Loader2, Banknote, Calendar, ChevronDown, ChevronUp,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Event types are built per-market so the energy-rating event reads
// 'BER Update' for IE, 'EPC Update' for UK, 'Energy Score Update' for US.
const buildEventTypes = (market) => {
  const energyLabel = market.code === 'GB' ? 'EPC Update'
                    : market.code === 'US' ? 'Energy Score Update'
                    : 'BER Update';
  return [
    { value: 'maintenance', label: 'Maintenance', icon: Wrench,    color: 'text-[#7042cc] bg-[#F5EFFF] border-[#7042cc]/20' },
    { value: 'renovation',  label: 'Renovation',  icon: Hammer,    color: 'text-amber-700 bg-amber-50 border-amber-200' },
    { value: 'valuation',   label: 'Valuation',   icon: TrendingUp,color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    { value: 'ber_update',  label: energyLabel,   icon: Award,     color: 'text-purple-700 bg-purple-50 border-purple-200' },
    { value: 'document',    label: 'Document',    icon: FileText,  color: 'text-gray-700 bg-gray-50 border-gray-200' },
    { value: 'inspection',  label: 'Inspection',  icon: Search,    color: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
  ];
};
const typeMeta = (t, types) => types.find((x) => x.value === t) || types[0];

const fmtDate = (iso, locale) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(locale || 'en-IE', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
};

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ReportButton = ({ propertyId, eventId }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 5) {
      toast({ title: 'Please give a short reason (min 5 chars).', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/api/passport/${propertyId}/events/${eventId}/report`, { reason: reason.trim() });
      setSent(true);
      toast({ title: 'Thanks — our team will review this entry.' });
    } catch (e) {
      toast({ title: 'Could not submit report', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  if (sent) return <span className="text-xs text-emerald-700" data-testid="passport-report-sent">Reported · thanks</span>;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-xs text-gray-500 hover:text-rose-600 inline-flex items-center gap-1"
        data-testid={`passport-report-btn-${eventId}`}
      >
        <Flag className="h-3 w-3" /> Report
      </button>
    );
  }

  return (
    <div className="mt-2 p-3 bg-rose-50 border border-rose-200 rounded-md space-y-2" data-testid={`passport-report-form-${eventId}`}>
      <Textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why is this entry misleading or false?"
        rows={2}
        maxLength={600}
        data-testid={`passport-report-reason-${eventId}`}
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setReason(''); }}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={sending} className="bg-rose-600 hover:bg-rose-700" data-testid={`passport-report-submit-${eventId}`}>
          {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Submit report'}
        </Button>
      </div>
    </div>
  );
};

const EventItem = ({ ev, canEdit, onDelete, propertyId, market, eventTypes }) => {
  const meta = typeMeta(ev.type, eventTypes);
  const Icon = meta.icon;
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-[#7042cc]/40 transition" data-testid={`passport-event-${ev.id}`}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center border ${meta.color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-semibold text-gray-900 truncate">{ev.title}</h4>
            <Badge variant="outline" className="text-xs shrink-0">{meta.label}</Badge>
          </div>
          {ev.description && (
            <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{ev.description}</p>
          )}
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
            {ev.date && (
              <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(ev.date, market.locale)}</span>
            )}
            {ev.cost != null && (
              <span className="inline-flex items-center gap-1"><Banknote className="h-3 w-3" /> {market.formatCurrency(ev.cost)}</span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-3">
            <ReportButton propertyId={propertyId} eventId={ev.id} />
            {canEdit && (
              <button
                onClick={() => onDelete(ev.id)}
                className="text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1"
                data-testid={`passport-delete-btn-${ev.id}`}
              >
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AddEventForm = ({ propertyId, onAdded, market, eventTypes }) => {
  const [showForm, setShowForm] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    type: 'maintenance',
    title: '',
    description: '',
    date: '',
    cost: '',
  });

  const submit = async () => {
    if (!form.title.trim()) {
      toast({ title: 'Title required', variant: 'destructive' });
      return;
    }
    setAdding(true);
    try {
      const body = {
        type: form.type,
        title: form.title.trim(),
        description: form.description.trim() || null,
        date: form.date ? new Date(form.date).toISOString() : null,
        cost: form.cost ? parseFloat(form.cost) : null,
      };
      const r = await axios.post(`${API}/api/passport/${propertyId}/events`, body, { headers: authHeaders() });
      toast({ title: 'Event added' });
      setForm({ type: 'maintenance', title: '', description: '', date: '', cost: '' });
      setShowForm(false);
      onAdded(r.data);
    } catch (e) {
      toast({ title: 'Could not add event', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setAdding(false);
    }
  };

  if (!showForm) {
    return (
      <Button
        onClick={() => setShowForm(true)}
        className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
        data-testid="passport-add-event-btn"
      >
        <Plus className="h-4 w-4 mr-2" /> Add event
      </Button>
    );
  }

  return (
    <div className="border border-[#7042cc]/30 bg-[#F5EFFF]/40 rounded-lg p-4 space-y-3" data-testid="passport-add-form">
      <div>
        <Label className="text-xs">Type</Label>
        <select
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
          data-testid="passport-event-type"
        >
          {eventTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-xs">Title</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="e.g. Replaced boiler"
          maxLength={120}
          data-testid="passport-event-title"
        />
      </div>
      <div>
        <Label className="text-xs">Description (optional)</Label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={2}
          maxLength={2000}
          data-testid="passport-event-description"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Date</Label>
          <Input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="passport-event-date"
          />
        </div>
        <div>
          <Label className="text-xs">Cost ({market.currency_symbol})</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={form.cost}
            onChange={(e) => setForm({ ...form, cost: e.target.value })}
            data-testid="passport-event-cost"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
        <Button onClick={submit} disabled={adding} className="bg-[#7042cc] hover:bg-[#5b35a8]" data-testid="passport-event-save">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save event'}
        </Button>
      </div>
    </div>
  );
};

export default function PassportInlinePanel({ propertyId, country }) {
  const { isAuthenticated } = useAuth();
  const market = useMarket(country);
  const eventTypes = useMemo(() => buildEventTypes(market), [market.code]);
  const [passport, setPassport] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    if (!propertyId) return;
    try {
      const [pp, ce] = await Promise.all([
        axios.get(`${API}/api/passport/${propertyId}`),
        isAuthenticated
          ? axios.get(`${API}/api/passport/${propertyId}/can-edit`, { headers: authHeaders() })
          : Promise.resolve({ data: { can_edit: false } }),
      ]);
      setPassport(pp.data);
      setCanEdit(!!ce.data?.can_edit);
    } catch (e) {
      // non-fatal — show empty state
      setPassport({ events: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [propertyId, isAuthenticated]);

  const onAdded = (ev) => setPassport((p) => ({ ...(p || {}), events: [...(p?.events || []), ev] }));

  const onDelete = async (eventId) => {
    if (!window.confirm('Delete this passport event?')) return;
    try {
      await axios.delete(`${API}/api/passport/${propertyId}/events/${eventId}`, { headers: authHeaders() });
      setPassport((p) => ({ ...p, events: (p?.events || []).filter((e) => e.id !== eventId) }));
      toast({ title: 'Event deleted' });
    } catch (e) {
      toast({ title: 'Could not delete', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const events = (passport?.events || []).slice().sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at));
  const visible = expanded ? events : events.slice(0, 3);

  return (
    <Card data-testid="passport-inline-panel">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <BookOpen className="h-6 w-6 text-[#7042cc] flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900">Owner&apos;s Logbook</h3>
              <p className="text-sm text-gray-600 mt-0.5">
                The home&apos;s lifetime service history — maintenance, renovations, valuations, {market.code === 'GB' ? 'EPC' : market.code === 'US' ? 'Energy Score' : 'BER'} updates.
                {!canEdit && ' This logbook can be edited by the homeowner or by their agent.'}
              </p>
            </div>
          </div>
          {canEdit && <AddEventForm propertyId={propertyId} onAdded={onAdded} market={market} eventTypes={eventTypes} />}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center" data-testid="passport-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading passport…
          </div>
        ) : events.length === 0 ? (
          <div className="text-sm text-gray-500 py-4 border border-dashed border-gray-200 rounded-lg text-center" data-testid="passport-empty">
            No events yet.{canEdit ? ' Add the first one above.' : ' The owner hasn\'t logged anything on this home yet.'}
          </div>
        ) : (
          <div className="space-y-3" data-testid="passport-events-list">
            {visible.map((ev) => (
              <EventItem key={ev.id} ev={ev} canEdit={canEdit} onDelete={onDelete} propertyId={propertyId} market={market} eventTypes={eventTypes} />
            ))}
            {events.length > 3 && (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="text-sm text-[#7042cc] hover:underline inline-flex items-center gap-1"
                data-testid="passport-toggle-expand"
              >
                {expanded ? <>Show less <ChevronUp className="h-3 w-3" /></> : <>Show all {events.length} events <ChevronDown className="h-3 w-3" /></>}
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
